import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";

import { Case, CaseReportSignature, User } from "pwapi";

import { CaseDiagnosis } from "custom-models";

import {
  cancelEditScreenDiagnoses,
  cancelModifyReportProcedure,
} from "shared/actions/caseDetailActions";
import { fetchCaseDisclaimers } from "shared/actions/caseReportActions";
import { FeatureFlags } from "shared/reducers/sysReducer";
import { isCaseTCOnly } from "shared/utils/caseUtils";

import CaseReport, {
  DisclaimersObject,
} from "components/caseReport/CaseReport";
import { getDiagnosesObject } from "components/caseReport/utils";
import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import RecallReportButton from "components/RecallReportButton";
import Spinner from "components/Spinner";
import SignOutButton from "components/viewer/dialogs/SignOutButton";
import {
  canSignoutCase,
  isModifyingReport,
  isScreened,
} from "components/viewer/dialogs/utils";
import ApplicationState from "store/ApplicationState";

const CaseReportPage = () => {
  const dispatch = useDispatch();
  const caseDetailObject = useSelector(
    (state: ApplicationState) => state.caseDetail
  );
  const slideDetailObject = useSelector(
    (state: ApplicationState) => state.slideDetail
  );
  const features: FeatureFlags = useSelector(
    ({ sys }: ApplicationState) => sys.features
  );
  const currentUser: User | undefined = useSelector(
    ({ auth }: ApplicationState) => auth.user
  );
  const { userSelectedLocationCode } = useSelector(
    ({ locationCodes }: ApplicationState) => locationCodes
  );

  const [finalSignature, setFinalSignature] = useState<
    CaseReportSignature | undefined
  >();

  const [isPrelimReport, setIsPrelimReport] = useState<boolean>(false);
  const [isTechnicalReport, setIsTechnicalReport] = useState<boolean>(
    (features?.technicalReportsOnly && isCaseTCOnly(caseDetailObject)) || false
  );
  const [disclaimers, setDisclaimers] = useState<DisclaimersObject[]>([]);

  const isModifying = isModifyingReport(
    caseDetailObject,
    features,
    currentUser
  );

  const previewState = canSignoutCase(
    caseDetailObject,
    currentUser,
    features?.signOutInViewer
  )
    ? "signout"
    : isModifying?.source
    ? "addon"
    : "preview";

  const diagnoses = getDiagnosesObject(
    caseDetailObject.specimenDetail,
    previewState,
    currentUser
  );

  useEffect(() => {
    if (caseDetailObject?.id) {
      dispatch(fetchCaseDisclaimers({ case_id: caseDetailObject.id })).then(
        (fetchedDisclaimers) => {
          setDisclaimers(
            (fetchedDisclaimers[0].disclaimers as DisclaimersObject[]) || []
          );
        }
      );
    }
  }, [caseDetailObject.id]);

  const onHandleCancelReportModification = () => {
    caseDetailObject?.id &&
      dispatch(cancelModifyReportProcedure(caseDetailObject.id));
  };

  const onHandleCancelScreenEdit = () => {
    caseDetailObject.id &&
      dispatch(cancelEditScreenDiagnoses(caseDetailObject.id));
  };

  return !caseDetailObject ? (
    <Spinner />
  ) : previewState === "signout" ? (
    <CaseReport
      disclaimersDryRun={disclaimers}
      caseDetailObject={caseDetailObject}
      diagnoses={diagnoses}
      currentUser={currentUser}
      features={features}
      isUnsigned
      selectedSignature={finalSignature}
      onChangeSignature={setFinalSignature}
      locationCode={userSelectedLocationCode}
      isPrelimReport={isPrelimReport}
      isTechnicalReport={
        (features?.technicalReportsOnly ||
          (features?.technicalReports && isTechnicalReport)) &&
        isCaseTCOnly(caseDetailObject)
      }
      actions={
        <>
          {!features?.screenPerSpecimen &&
            isScreened(caseDetailObject.specimenDetail[0]?.diagnoses) &&
            caseDetailObject.specimenDetail[0]?.diagnoses.find(
              (diag) =>
                diag.user &&
                diag.user.id === currentUser?.id &&
                !diag.is_completed
            ) && (
              <PathologyWatchButton
                color="red"
                onClick={onHandleCancelScreenEdit}
              >
                Cancel Screen Edit
              </PathologyWatchButton>
            )}

          {caseDetailObject.source === Case.source.PW &&
            features?.technicalReports &&
            isCaseTCOnly(caseDetailObject) && (
              <label className="Input__checkbox">
                <input
                  type="checkbox"
                  onChange={() => {
                    setIsTechnicalReport((s) => !s);
                    setIsPrelimReport(false);
                  }}
                  checked={isTechnicalReport}
                />
                Preview Technical Report
              </label>
            )}
          {features?.issuePrelimReports &&
            !features?.technicalReportsOnly &&
            caseDetailObject.source === Case.source.PW && (
              <label className="Input__checkbox">
                <input
                  type="checkbox"
                  onChange={() => {
                    setIsPrelimReport((s) => !s);
                    setIsTechnicalReport(false);
                  }}
                  checked={isPrelimReport}
                />
                Preview Preliminary Report
              </label>
            )}
          <SignOutButton
            caseDetailObject={caseDetailObject}
            currentSlideId={slideDetailObject?.id}
            user={currentUser}
            selectedSignatureId={finalSignature?.id}
            isPrelim={isPrelimReport}
            signOutTipPlace="left"
            isTechnical={
              (features?.technicalReportsOnly ||
                (features?.technicalReports && isTechnicalReport)) &&
              isCaseTCOnly(caseDetailObject)
            }
          />
        </>
      }
    />
  ) : isModifying?.source ? (
    <CaseReport
      disclaimersDryRun={disclaimers}
      caseDetailObject={caseDetailObject}
      diagnoses={diagnoses}
      currentUser={currentUser}
      features={features}
      isUnsigned
      selectedSignature={finalSignature}
      onChangeSignature={setFinalSignature}
      useChangeAddon={isModifying.source !== CaseDiagnosis.source.CORRECTION}
      actions={
        <>
          <PathologyWatchButton
            onClick={onHandleCancelReportModification}
            color="red"
          >
            {`Cancel ${isModifying.source}`}
          </PathologyWatchButton>
          <SignOutButton
            caseDetailObject={caseDetailObject}
            currentSlideId={slideDetailObject?.id}
            user={currentUser}
            selectedSignatureId={finalSignature?.id}
            signOutTipPlace="left"
            label={`${
              isModifying.source === CaseDiagnosis.source.CORRECTION
                ? "Correct"
                : isModifying.source
            } Report`}
          />
        </>
      }
    />
  ) : (
    <CaseReport
      disclaimersDryRun={disclaimers}
      diagnoses={diagnoses}
      currentUser={currentUser}
      isUnsigned
      features={features}
      caseDetailObject={caseDetailObject}
      actions={
        caseDetailObject.status === Case.status.LIS_PENDING ? (
          <RecallReportButton
            currentUser={currentUser}
            caseDetail={caseDetailObject}
          />
        ) : undefined
      }
    />
  );
};

export default CaseReportPage;
